import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wrench,
  Plus,
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  Clock,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  PageHeader,
  SearchInput,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  ConfirmDialog,
} from '@/components/common';
import {
  equipmentApi,
  Equipment as EquipmentType,
  CreateEquipmentDto,
  QueryEquipmentParams,
  EquipmentDashboard,
  EquipmentCategory,
  EquipmentStatus,
  EquipmentCondition,
} from '@/api/services/equipment.api';
import { locationsApi, Location } from '@/api/services';
import { getErrorMessage } from '@/api/client';

const categories: EquipmentCategory[] = [
  'DRILLING',
  'COMPLETION',
  'PRODUCTION',
  'TESTING',
  'SAFETY',
  'TRANSPORTATION',
  'OTHER',
];

const statuses: EquipmentStatus[] = [
  'AVAILABLE',
  'IN_USE',
  'MAINTENANCE',
  'REPAIR',
  'RETIRED',
  'DISPOSED',
];

const conditions: EquipmentCondition[] = [
  'EXCELLENT',
  'GOOD',
  'FAIR',
  'POOR',
  'CRITICAL',
];

interface EquipmentFormData {
  assetNumber: string;
  name: string;
  description: string;
  category: EquipmentCategory;
  manufacturer: string;
  model: string;
  serialNumber: string;
  locationId: string;
  status: EquipmentStatus;
  condition: EquipmentCondition;
  purchaseDate: string;
  purchasePrice: string;
  warrantyExpiry: string;
  operatingHours: string;
}

const emptyFormData: EquipmentFormData = {
  assetNumber: '',
  name: '',
  description: '',
  category: 'OTHER',
  manufacturer: '',
  model: '',
  serialNumber: '',
  locationId: '',
  status: 'AVAILABLE',
  condition: 'GOOD',
  purchaseDate: '',
  purchasePrice: '',
  warrantyExpiry: '',
  operatingHours: '',
};

export default function Equipment() {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState<EquipmentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<EquipmentDashboard | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    item?: EquipmentType;
  }>({ open: false });
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<EquipmentType | null>(null);
  const [formData, setFormData] = useState<EquipmentFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);

  // Reference data
  const [locations, setLocations] = useState<Location[]>([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params: QueryEquipmentParams = {
        search: searchQuery || undefined,
        page: pagination.page,
        limit: pagination.limit,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter as EquipmentStatus;
      }
      if (categoryFilter !== 'all') {
        params.category = categoryFilter as EquipmentCategory;
      }

      const [equipmentRes, dashboardRes] = await Promise.all([
        equipmentApi.getAll(params),
        equipmentApi.getDashboard(),
      ]);

      setEquipment(equipmentRes.data.data);
      setPagination(equipmentRes.data.pagination);
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, categoryFilter, pagination.page, pagination.limit]);

  const fetchLocations = async () => {
    try {
      const res = await locationsApi.getAll({ limit: 500 });
      setLocations(res.data);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchLocations();
  }, [fetchData]);

  const handleOpenForm = (item?: EquipmentType) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        assetNumber: item.assetNumber,
        name: item.name,
        description: item.description || '',
        category: item.category,
        manufacturer: item.manufacturer || '',
        model: item.model || '',
        serialNumber: item.serialNumber || '',
        locationId: item.locationId,
        status: item.status,
        condition: item.condition,
        purchaseDate: item.purchaseDate?.split('T')[0] || '',
        purchasePrice: item.purchasePrice?.toString() || '',
        warrantyExpiry: item.warrantyExpiry?.split('T')[0] || '',
        operatingHours: item.operatingHours?.toString() || '',
      });
    } else {
      setEditingItem(null);
      setFormData(emptyFormData);
    }
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.assetNumber || !formData.name || !formData.locationId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const dto: CreateEquipmentDto = {
        assetNumber: formData.assetNumber,
        name: formData.name,
        description: formData.description || undefined,
        category: formData.category,
        manufacturer: formData.manufacturer || undefined,
        model: formData.model || undefined,
        serialNumber: formData.serialNumber || undefined,
        locationId: formData.locationId,
        status: formData.status,
        condition: formData.condition,
        purchaseDate: formData.purchaseDate || undefined,
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : undefined,
        warrantyExpiry: formData.warrantyExpiry || undefined,
        operatingHours: formData.operatingHours ? parseFloat(formData.operatingHours) : undefined,
      };

      if (editingItem) {
        await equipmentApi.update(editingItem.id, dto);
        toast.success('Equipment updated successfully');
      } else {
        await equipmentApi.create(dto);
        toast.success('Equipment created successfully');
      }

      setFormOpen(false);
      setFormData(emptyFormData);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;

    try {
      setDeleting(true);
      await equipmentApi.delete(deleteDialog.item.id);
      toast.success('Equipment deleted successfully');
      setDeleteDialog({ open: false });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column<EquipmentType>[] = [
    {
      key: 'asset',
      header: 'Asset',
      render: (row: EquipmentType) => (
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="font-medium">{row.name}</p>
            <p className="text-xs text-muted-foreground">{row.assetNumber}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (row: EquipmentType) => (
        <span className="text-sm">{row.category.replace('_', ' ')}</span>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (row: EquipmentType) => (
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <span>{row.location.name}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row: EquipmentType) => (
        <StatusBadge
          status={row.status.toLowerCase().replace(/_/g, '_')}
          label={row.status.replace(/_/g, ' ')}
        />
      ),
    },
    {
      key: 'condition',
      header: 'Condition',
      render: (row: EquipmentType) => (
        <StatusBadge
          status={row.condition.toLowerCase()}
          label={row.condition}
        />
      ),
    },
    {
      key: 'nextServiceDate',
      header: 'Next Service',
      render: (row: EquipmentType) =>
        row.nextServiceDate ? (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>{new Date(row.nextServiceDate).toLocaleDateString()}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (row: EquipmentType) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/equipment/${row.id}`)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleOpenForm(row)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, item: row })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Equipment"
        description="Manage equipment and assets"
        actions={
          <Button onClick={() => handleOpenForm()}>
            <Plus className="mr-2 h-4 w-4" />
            Add Equipment
          </Button>
        }
      />

      {/* Dashboard Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Equipment"
          value={dashboard?.total || 0}
          trend={{ direction: 'neutral', value: `${dashboard?.byStatus?.AVAILABLE || 0} available` }}
        />
        <MetricCard
          label="In Use"
          value={dashboard?.byStatus?.IN_USE || 0}
          trend={{ direction: 'neutral', value: `${dashboard?.utilizationRate || 0}% utilization` }}
        />
        <MetricCard
          label="Maintenance Due"
          value={dashboard?.maintenanceDue || 0}
          trend={{ direction: 'neutral', value: 'Within 7 days' }}
        />
        <MetricCard
          label="In Repair"
          value={(dashboard?.byStatus?.REPAIR || 0) + (dashboard?.byStatus?.MAINTENANCE || 0)}
          trend={{ direction: 'neutral', value: 'Currently unavailable' }}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search equipment..."
          className="w-full sm:w-64"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {status.replace('_', ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat.replace('_', ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={equipment}
        loading={loading}
        onRowClick={(row) => navigate(`/equipment/${row.id}`)}
      />

      {/* Create/Edit Form Sheet */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingItem ? 'Edit Equipment' : 'Add Equipment'}</SheetTitle>
            <SheetDescription>
              {editingItem ? 'Update equipment details' : 'Add a new piece of equipment'}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="assetNumber">Asset Number *</Label>
                <Input
                  id="assetNumber"
                  value={formData.assetNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, assetNumber: e.target.value }))
                  }
                  disabled={!!editingItem}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v: EquipmentCategory) =>
                    setFormData((prev) => ({ ...prev, category: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="manufacturer">Manufacturer</Label>
                <Input
                  id="manufacturer"
                  value={formData.manufacturer}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, manufacturer: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData((prev) => ({ ...prev, model: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="serialNumber">Serial Number</Label>
              <Input
                id="serialNumber"
                value={formData.serialNumber}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, serialNumber: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location *</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData((prev) => ({ ...prev, locationId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v: EquipmentStatus) =>
                    setFormData((prev) => ({ ...prev, status: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="condition">Condition</Label>
                <Select
                  value={formData.condition}
                  onValueChange={(v: EquipmentCondition) =>
                    setFormData((prev) => ({ ...prev, condition: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {conditions.map((cond) => (
                      <SelectItem key={cond} value={cond}>
                        {cond}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="purchaseDate">Purchase Date</Label>
                <Input
                  id="purchaseDate"
                  type="date"
                  value={formData.purchaseDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, purchaseDate: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purchasePrice">Purchase Price</Label>
                <Input
                  id="purchasePrice"
                  type="number"
                  value={formData.purchasePrice}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, purchasePrice: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="warrantyExpiry">Warranty Expiry</Label>
                <Input
                  id="warrantyExpiry"
                  type="date"
                  value={formData.warrantyExpiry}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, warrantyExpiry: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="operatingHours">Operating Hours</Label>
                <Input
                  id="operatingHours"
                  type="number"
                  value={formData.operatingHours}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, operatingHours: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : editingItem ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        title="Delete Equipment"
        description={`Are you sure you want to delete "${deleteDialog.item?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
}
